import { useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import WhyChooseUs from './components/WhyChooseUs';
import Testimonials from './components/Testimonials';
import Gallery from './components/Gallery';
import ServiceAreas from './components/ServiceAreas';
import EstimateForm from './components/EstimateForm';
import Contact from './components/Contact';
import Footer from './components/Footer';

export default function HomePage() {
  useEffect(() => {
    // Handle scroll-to-estimate from service pages
    const shouldScroll = sessionStorage.getItem('scrollToEstimate');
    if (shouldScroll) {
      sessionStorage.removeItem('scrollToEstimate');
      setTimeout(() => {
        const el = document.getElementById('estimate');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://americandreamremodelinc.com';

    const localBusiness = {
      '@context': 'https://schema.org',
      '@type': 'HomeAndConstructionBusiness',
      name: 'American Dream Remodeling INC',
      url: siteUrl,
      telephone: '+1-586-443-0294',
      email: 'info@americandreamremodelinc.com',
      description:
        'American Dream Remodeling INC specializes in bathroom remodeling, basement finishing, and tile installation in Macomb, Oakland, Wayne and Washtenaw County, Michigan.',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Detroit',
        addressRegion: 'MI',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 42.331427,
        longitude: -83.045754,
      },
      areaServed: [
        { '@type': 'County', name: 'Macomb County' },
        { '@type': 'County', name: 'Oakland County' },
        { '@type': 'County', name: 'Wayne County' },
        { '@type': 'County', name: 'Washtenaw County' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Remodeling Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Bathroom Remodeling' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Basement Finishing' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Tile Installation' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kitchen Remodeling' } },
        ],
      },
      priceRange: '$$',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday'],
          opens: '09:00',
          closes: '14:00',
        },
      ],
    };

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'American Dream Remodeling INC',
      url: siteUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}/?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Services',
          item: `${siteUrl}/#services`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Get a Free Estimate',
          item: `${siteUrl}/#estimate`,
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: 'Contact',
          item: `${siteUrl}/#contact`,
        },
      ],
    };

    const faqPage = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do you offer free estimates for bathroom remodeling in Detroit?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes! American Dream Remodeling INC provides free, no-obligation estimates for all bathroom remodeling and basement finishing projects in Southeast Michigan.',
          },
        },
        {
          '@type': 'Question',
          name: 'What areas do you serve for basement finishing?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We serve Macomb County, Oakland County, Wayne County, and Washtenaw County for basement finishing, bathroom remodeling, and tile installation.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are you licensed and insured for tile installation and remodeling work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, American Dream Remodeling INC is fully insured and has over 10 years of experience delivering quality bathroom remodeling and tile installation across Southeast Michigan.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does a typical bathroom remodeling project take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most bathroom remodeling projects are completed within 1–2 weeks depending on the scope of work. Basement finishing projects typically take 3–6 weeks. We provide a detailed timeline during your free estimate.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you handle permits for remodeling projects in Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, American Dream Remodeling INC handles all necessary permits for bathroom remodeling, basement finishing, and tile installation projects throughout Southeast Michigan.',
          },
        },
      ],
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema('schema-local-business', localBusiness);
    injectSchema('schema-website', websiteSchema);
    injectSchema('schema-breadcrumb', breadcrumb);
    injectSchema('schema-faq', faqPage);

    return () => {
      ['schema-local-business', 'schema-website', 'schema-breadcrumb', 'schema-faq'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Hero />
      <Services />
      <Gallery />
      <WhyChooseUs />
      <Testimonials />
      <ServiceAreas />
      <EstimateForm />
      <Contact />
      <Footer />
    </div>
  );
}
